/**
 * Users Tab Component - User Management
 * Features: User list, user detail modal, notes, delete, activity chart
 */
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    Users, Shield, Calendar, UserCheck, BarChart3, Eye, 
    ChevronLeft, ChevronRight, CheckCircle, XCircle, Loader2,
    Trash2, StickyNote
} from 'lucide-react';
import { getAuthHeaders } from '../../utils/api';
import { toast } from 'sonner';
import { BentoCard, StatDisplay, NeonIcon, API_URL } from './shared';

/**
 * User Detail Modal - For viewing, editing notes, and deleting users
 */
const UserDetailModal = ({ user, onClose, onDelete, onSaveNote, isSpanish }) => {
    const [note, setNote] = useState(user?.admin_note || '');
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    
    const handleSaveNote = async () => {
        setSaving(true);
        await onSaveNote(user.user_id, note);
        setSaving(false);
    };
    
    const handleDelete = async () => {
        setDeleting(true);
        await onDelete(user.user_id, user.email);
        setDeleting(false);
        onClose();
    };
    
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-[#12151c] border border-primary/30 rounded-2xl p-6 max-w-md w-full max-h-[80vh] overflow-y-auto"
            >
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                            <NeonIcon icon={Users} neonColor="purple" size="w-6 h-6" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-white">{user?.name || 'Unknown'}</h3>
                            <p className="text-sm text-white/50">{user?.email}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-white/50 hover:text-white">✕</button>
                </div>
                
                <div className="space-y-3 mb-6">
                    <div className="flex justify-between py-2 border-b border-white/10">
                        <span className="text-white/60">{isSpanish ? 'Idioma' : 'Language'}</span>
                        <span className={`px-2 py-0.5 rounded text-xs ${user?.language === 'es' ? 'bg-orange-500/20 text-orange-400' : 'bg-blue-500/20 text-blue-400'}`}>
                            {user?.language === 'es' ? 'Español' : 'English'}
                        </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-white/10">
                        <span className="text-white/60">{isSpanish ? 'Signo Solar' : 'Sun Sign'}</span>
                        <span className="text-primary capitalize">{user?.sun_sign || '-'}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-white/10">
                        <span className="text-white/60">{isSpanish ? 'Uso de Oracle' : 'Oracle Usage'}</span>
                        <span className="text-white">{user?.oracle_usage || 0}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-white/10">
                        <span className="text-white/60">{isSpanish ? 'Registrado' : 'Joined'}</span>
                        <span className="text-white/80 text-sm">{user?.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-white/10">
                        <span className="text-white/60">{isSpanish ? 'Datos completos' : 'Data Complete'}</span>
                        {user?.birth_data_complete ? <CheckCircle className="w-5 h-5 text-green-400" /> : <XCircle className="w-5 h-5 text-red-400/50" />}
                    </div>
                </div>
                
                <div className="mb-6">
                    <label className="flex items-center gap-2 text-sm text-white/70 mb-2">
                        <NeonIcon icon={StickyNote} neonColor="yellow" size="w-4 h-4" />
                        {isSpanish ? 'Notas de Admin' : 'Admin Notes'}
                    </label>
                    <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder={isSpanish ? "Agregar notas sobre este usuario..." : "Add notes about this user..."}
                        className="w-full h-24 px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/30 text-sm resize-none focus:border-primary/50 focus:outline-none"
                    />
                    <button onClick={handleSaveNote} disabled={saving}
                        className="mt-2 px-4 py-1.5 bg-primary/20 text-primary rounded-lg text-sm hover:bg-primary/30 transition-colors flex items-center gap-2 disabled:opacity-50">
                        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                        {isSpanish ? 'Guardar Nota' : 'Save Note'}
                    </button>
                </div>
                
                <div className="pt-4 border-t border-white/10">
                    {!showDeleteConfirm ? (
                        <button onClick={() => setShowDeleteConfirm(true)}
                            className="w-full py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors flex items-center justify-center gap-2">
                            <Trash2 className="w-4 h-4" />
                            {isSpanish ? 'Eliminar Usuario' : 'Delete User'}
                        </button>
                    ) : (
                        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                            <p className="text-red-400 text-sm mb-3">
                                {isSpanish ? '⚠️ Esta acción eliminará permanentemente al usuario y todos sus datos. Se enviará un email de confirmación.' : '⚠️ This will permanently delete the user and all their data. A confirmation email will be sent.'}
                            </p>
                            <div className="flex gap-2">
                                <button onClick={() => setShowDeleteConfirm(false)} className="flex-1 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20">
                                    {isSpanish ? 'Cancelar' : 'Cancel'}
                                </button>
                                <button onClick={handleDelete} disabled={deleting}
                                    className="flex-1 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center justify-center gap-2 disabled:opacity-50">
                                    {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                    {isSpanish ? 'Confirmar' : 'Confirm Delete'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </motion.div>
        </motion.div>
    );
};

const UsersTab = ({ users, usersStats, usersTotal, usersPage, usersLoading, fetchUsers, isSpanish }) => {
    const totalPages = Math.ceil(usersTotal / 20);
    const [activityData, setActivityData] = useState(null);
    const [activityLoading, setActivityLoading] = useState(true);
    const [selectedUser, setSelectedUser] = useState(null);
    
    const handleDeleteUser = async (userId, userEmail) => {
        try {
            const authHeaders = await getAuthHeaders();
            const headers = {
                ...authHeaders,
                'X-Internal-Secret': process.env.REACT_APP_INTERNAL_SECRET || ''
            };
            const res = await fetch(`${API_URL}/api/admin/users/${userId}`, { method: 'DELETE', headers, credentials: 'include' });
            if (res.ok) { 
                toast.success(isSpanish ? 'Usuario eliminado correctamente' : 'User deleted successfully'); 
                fetchUsers(usersPage); 
            } else { 
                const data = await res.json().catch(() => ({})); 
                toast.error(data.detail || (isSpanish ? 'Error al eliminar usuario' : 'Failed to delete user')); 
            }
        } catch (e) { 
            console.error('Delete user error:', e);
            toast.error(isSpanish ? 'Error al eliminar usuario' : 'Failed to delete user'); 
        }
    };
    
    const handleSaveNote = async (userId, note) => {
        try {
            const authHeaders = await getAuthHeaders();
            const headers = {
                ...authHeaders,
                'Content-Type': 'application/json',
                'X-Internal-Secret': process.env.REACT_APP_INTERNAL_SECRET || ''
            };
            const res = await fetch(`${API_URL}/api/admin/users/${userId}/note`, {
                method: 'PUT', headers, credentials: 'include', body: JSON.stringify({ note })
            });
            if (res.ok) toast.success(isSpanish ? 'Nota guardada' : 'Note saved');
            else toast.error(isSpanish ? 'Error al guardar nota' : 'Failed to save note');
        } catch (e) { toast.error(isSpanish ? 'Error al guardar nota' : 'Failed to save note'); }
    };
    
    useEffect(() => {
        const fetchActivity = async () => {
            try {
                const authHeaders = await getAuthHeaders();
                const headers = {
                    ...authHeaders,
                    'X-Internal-Secret': process.env.REACT_APP_INTERNAL_SECRET || ''
                };
                const res = await fetch(`${API_URL}/api/admin/users/activity?days=30`, { headers, credentials: 'include' });
                if (res.ok) setActivityData(await res.json());
            } catch (e) { console.error('Activity fetch error:', e); }
            finally { setActivityLoading(false); }
        };
        fetchActivity();
    }, []);
    
    const maxSignups = activityData?.daily_data?.length > 0 ? Math.max(...activityData.daily_data.map(d => d.signups), 1) : 1;
    
    return (
        <div className="space-y-4">
            <AnimatePresence>
                {selectedUser && <UserDetailModal user={selectedUser} onClose={() => setSelectedUser(null)} onDelete={handleDeleteUser} onSaveNote={handleSaveNote} isSpanish={isSpanish} />}
            </AnimatePresence>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <BentoCard><StatDisplay label={isSpanish ? "Total Usuarios" : "Total Users"} value={usersStats?.total_users?.toLocaleString() || usersTotal?.toLocaleString() || '0'} icon={Users} color="primary" /></BentoCard>
                <BentoCard><StatDisplay label={isSpanish ? "Nuevos Esta Semana" : "New This Week"} value={usersStats?.new_this_week?.toLocaleString() || '0'} icon={UserCheck} color="green-400" /></BentoCard>
                <BentoCard><StatDisplay label={isSpanish ? "Con Datos Completos" : "With Complete Data"} value={users.filter(u => u.birth_data_complete).length} icon={Calendar} color="blue-400" /></BentoCard>
                <BentoCard><StatDisplay label={isSpanish ? "Admins" : "Admins"} value={users.filter(u => u.is_admin).length} icon={Shield} color="yellow-400" /></BentoCard>
            </div>
            
            <BentoCard className="col-span-4">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-white flex items-center gap-2"><NeonIcon icon={BarChart3} neonColor="cyan" size="w-4 h-4" />{isSpanish ? 'Crecimiento de Usuarios (30 días)' : 'User Growth (30 days)'}</h3>
                    {activityData && (
                        <div className="flex items-center gap-4 text-sm">
                            <span className="text-white/50">{isSpanish ? 'Total:' : 'Total:'}<span className="text-primary ml-1">{activityData.total_signups}</span></span>
                            <span className="text-white/50">{isSpanish ? 'Promedio:' : 'Avg:'}<span className="text-green-400 ml-1">{activityData.average_daily}/day</span></span>
                        </div>
                    )}
                </div>
                {activityLoading ? (
                    <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 text-primary animate-spin" /></div>
                ) : activityData?.daily_data?.length > 0 ? (
                    <div className="space-y-2">
                        <div className="flex items-end gap-1 h-32" data-testid="user-growth-chart">
                            {activityData.daily_data.slice(-30).map((day, i) => {
                                const height = maxSignups > 0 ? (day.signups / maxSignups) * 100 : 0;
                                const isToday = i === activityData.daily_data.length - 1;
                                return (
                                    <div key={day.date} className="flex-1 group relative" title={`${day.date}: ${day.signups} signups`}>
                                        <div className={`w-full rounded-t transition-all ${isToday ? 'bg-primary' : day.signups > 0 ? 'bg-primary/60 hover:bg-primary/80' : 'bg-white/10'}`}
                                            style={{ height: `${Math.max(height, 2)}%` }} />
                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                                            <div className="bg-black/90 border border-white/20 rounded px-2 py-1 text-xs whitespace-nowrap">
                                                <div className="text-white">{day.signups} {isSpanish ? 'registros' : 'signups'}</div>
                                                <div className="text-white/50">{new Date(day.date).toLocaleDateString()}</div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="flex justify-between text-xs text-white/40 px-1">
                            <span>{activityData.daily_data[0]?.date?.slice(5)}</span>
                            <span>{isSpanish ? 'Hoy' : 'Today'}</span>
                        </div>
                        {activityData.best_day && activityData.best_day.signups > 0 && (
                            <div className="mt-3 p-2 bg-green-500/10 border border-green-500/20 rounded-lg text-sm">
                                <span className="text-green-400">★ {isSpanish ? 'Mejor día:' : 'Best day:'}</span>
                                <span className="text-white ml-2">{new Date(activityData.best_day.date).toLocaleDateString()} - {activityData.best_day.signups} {isSpanish ? 'registros' : 'signups'}</span>
                            </div>
                        )}
                    </div>
                ) : (
                    <p className="text-white/40 text-sm text-center py-4">{isSpanish ? 'No hay datos de actividad aún' : 'No activity data yet'}</p>
                )}
            </BentoCard>
            
            <BentoCard className="col-span-4">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-white flex items-center gap-2"><NeonIcon icon={Users} neonColor="purple" size="w-4 h-4" />{isSpanish ? 'Lista de Usuarios' : 'User List'}</h3>
                    <div className="flex items-center gap-2">
                        <button onClick={() => fetchUsers(Math.max(0, usersPage - 1))} disabled={usersPage === 0 || usersLoading} className="p-2 bg-white/5 rounded-lg hover:bg-white/10 disabled:opacity-30">
                            <ChevronLeft className="w-4 h-4 text-white/70" />
                        </button>
                        <span className="text-sm text-white/50">{isSpanish ? 'Página' : 'Page'} {usersPage + 1} / {totalPages || 1}</span>
                        <button onClick={() => fetchUsers(usersPage + 1)} disabled={usersPage >= totalPages - 1 || usersLoading} className="p-2 bg-white/5 rounded-lg hover:bg-white/10 disabled:opacity-30">
                            <ChevronRight className="w-4 h-4 text-white/70" />
                        </button>
                    </div>
                </div>
                {usersLoading ? (
                    <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="users-table">
                            <thead>
                                <tr className="text-white/50 text-xs uppercase border-b border-white/10">
                                    <th className="text-left py-3 px-2">{isSpanish ? 'Usuario' : 'User'}</th>
                                    <th className="text-left py-3 px-2">Email</th>
                                    <th className="text-center py-3 px-2">{isSpanish ? 'Idioma' : 'Language'}</th>
                                    <th className="text-center py-3 px-2">{isSpanish ? 'Signo' : 'Sign'}</th>
                                    <th className="text-center py-3 px-2">{isSpanish ? 'Datos' : 'Data'}</th>
                                    <th className="text-center py-3 px-2">{isSpanish ? 'Uso Oracle' : 'Oracle Use'}</th>
                                    <th className="text-left py-3 px-2">{isSpanish ? 'Registrado' : 'Joined'}</th>
                                    <th className="text-center py-3 px-2">{isSpanish ? 'Acciones' : 'Actions'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((user, i) => (
                                    <tr key={i} className="border-t border-white/5 hover:bg-white/5 cursor-pointer" onClick={() => setSelectedUser(user)}>
                                        <td className="py-3 px-2">
                                            <div className="flex items-center gap-2">
                                                <span className="text-white font-medium">{user.name || 'Unknown'}</span>
                                                {user.is_admin && <Shield className="w-3 h-3 text-yellow-400" title="Admin" />}
                                                {user.admin_note && <StickyNote className="w-3 h-3 text-primary/50" title="Has notes" />}
                                            </div>
                                        </td>
                                        <td className="py-3 px-2 text-white/60">{user.email || '-'}</td>
                                        <td className="py-3 px-2 text-center">
                                            <span className={`px-2 py-0.5 rounded text-xs ${user.language === 'es' ? 'bg-orange-500/20 text-orange-400' : 'bg-blue-500/20 text-blue-400'}`}>
                                                {user.language === 'es' ? 'ES' : 'EN'}
                                            </span>
                                        </td>
                                        <td className="py-3 px-2 text-center text-white/70 capitalize">{user.sun_sign || '-'}</td>
                                        <td className="py-3 px-2 text-center">{user.birth_data_complete ? <CheckCircle className="w-4 h-4 text-green-400 mx-auto" /> : <XCircle className="w-4 h-4 text-red-400/50 mx-auto" />}</td>
                                        <td className="py-3 px-2 text-center text-primary">{user.oracle_usage || 0}</td>
                                        <td className="py-3 px-2 text-white/40 text-xs">{user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}</td>
                                        <td className="py-3 px-2 text-center" onClick={(e) => e.stopPropagation()}>
                                            <button onClick={(e) => { e.stopPropagation(); setSelectedUser(user); }}
                                                className="p-1.5 bg-primary/10 text-primary rounded hover:bg-primary/20 transition-colors" title={isSpanish ? 'Ver detalles' : 'View details'}>
                                                <Eye className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {users.length === 0 && <p className="text-white/40 text-sm text-center py-8">{isSpanish ? 'No se encontraron usuarios' : 'No users found'}</p>}
                    </div>
                )}
            </BentoCard>
        </div>
    );
};

export default UsersTab;
